/**
 * Hash router — no dependency, and the server can serve index.html for
 * every path without route tables. Routes:
 *   #/            home
 *   #/mod/123456  moderator
 *   #/mod/123456/<token>  moderator on a second device (2026-09-08): the
 *                 shareable link carries the moderation token, which is
 *                 stashed in localStorage and stripped from the address bar
 *                 so a screenshot of the URL does not hand it around.
 *   #/write/123456 author
 *   #/beam/123456 projection
 *   #/play        playback of a recorded round (2026-09-09) — local, no PIN:
 *                 the export is read in the browser, nothing is sent anywhere
 *   #/clip        vertical 9:16 clip of a recorded round (2026-09-09); with
 *                 `#/clip?render=1` it is the surface the frame renderer drives
 */

import { useEffect, useState } from 'react';

export type Route =
  | { view: 'home' }
  | { view: 'mod'; pin: string }
  | { view: 'write'; pin: string }
  | { view: 'beam'; pin: string }
  | { view: 'play' }
  | { view: 'clip' };

const ROUTE_RE = /^#\/(mod|write|beam)\/(\d{6})(?:\/([A-Za-z0-9_-]{8,64}))?/;
const PLAY_RE = /^#\/play\b/;
const CLIP_RE = /^#\/clip\b/;

export function parseRoute(hash: string): Route {
  if (PLAY_RE.test(hash)) return { view: 'play' };
  if (CLIP_RE.test(hash)) return { view: 'clip' };
  const m = ROUTE_RE.exec(hash);
  if (!m) return { view: 'home' };
  return { view: m[1] as 'mod' | 'write' | 'beam', pin: m[2]! };
}

/**
 * Claim the moderation chair from a shared link (2026-09-08) and strip the
 * token from the address bar, so a second moderator can simply open the URL
 * the main moderator sent them. Called once, before React renders.
 */
export function claimModeratorTokenFromUrl(): void {
  const m = ROUTE_RE.exec(location.hash);
  if (!m || m[1] !== 'mod' || !m[3]) return;
  storeModeratorToken(m[2]!, m[3]);
  history.replaceState(null, '', `${location.pathname}${location.search}#/mod/${m[2]}`);
}

export function navigate(hash: string): void {
  location.hash = hash;
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseRoute(location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}

/* --------- per-PIN credentials (survive refresh; reclaim seats, N-3) ----- */

export function storedModeratorToken(pin: string): string | null {
  return localStorage.getItem(`sa-mod-${pin}`);
}
export function storeModeratorToken(pin: string, token: string): void {
  localStorage.setItem(`sa-mod-${pin}`, token);
}
/** Shareable moderation link for a second device (2026-09-08). */
export function moderatorLink(pin: string, token: string): string {
  return `${location.origin}/#/mod/${pin}/${token}`;
}
export function storedAuthorToken(pin: string): string | null {
  return localStorage.getItem(`sa-author-${pin}`);
}
export function storeAuthorToken(pin: string, token: string): void {
  localStorage.setItem(`sa-author-${pin}`, token);
}
